/**
 * DraftWatermark — Phase 02 Task 02.7.
 *
 * Spec:
 *   docs/plans/2026-04-22-accountant-attestation/phases/phase-02-draft-final-status-machine/checklist.md
 *
 * Renders a diagonal "DRAFT" watermark over its content whenever the
 * report is NOT in `final` status (SM2 invariant). When status === "final"
 * the component renders nothing — the external attester has signed and
 * the report is attestable.
 *
 * Pointer-events: none so the watermark never intercepts clicks.
 */

import React from 'react';

export type DraftStatus =
  | 'draft'
  | 'in_progress'
  | 'ready_for_attorney'
  | 'final'
  | 'archived'
  | 'completed'
  | 'submitted'
  | string;

export interface DraftWatermarkProps {
  status: DraftStatus | undefined | null;
  label?: string;
}

const CONTAINER_STYLE: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  userSelect: 'none',
  zIndex: 50,
  overflow: 'hidden',
};

/**
 * The mark sits ON TOP of the content, so it has to stay under the threshold
 * where it competes with text. The old fixed `rgba(45,55,72,0.08)` was tuned
 * for a dark page: on the light theme's white surfaces the same navy reads as
 * legible gray letterforms dragged across the cards. `--color-text-ghost`
 * tracks the theme (#3A3A3A dark / #C8C8C8 light) and the extra opacity keeps
 * it a texture rather than a word.
 */
const BADGE_STYLE: React.CSSProperties = {
  fontFamily: 'DM Serif Display, serif',
  fontSize: 'clamp(4rem, 18vw, 12rem)',
  fontWeight: 700,
  color: 'var(--color-text-ghost)',
  opacity: 0.28,
  letterSpacing: '0.2em',
  transform: 'rotate(-30deg)',
  whiteSpace: 'nowrap',
};

/**
 * Returns true when this status is an attested-final state and the
 * watermark should be suppressed.
 *
 * Intentionally a single comparison today. Kept as a helper so future
 * callers (PDF export, attorney overlay) share the same rule.
 */
export function isExternallyAttested(status: DraftWatermarkProps['status']): boolean {
  return status === 'final';
}

export const DraftWatermark: React.FC<DraftWatermarkProps> = ({
  status,
  label = 'DRAFT',
}) => {
  if (isExternallyAttested(status)) {
    return null;
  }

  return (
    <div
      aria-hidden="true"
      data-testid="draft-watermark"
      style={CONTAINER_STYLE}
    >
      <span style={BADGE_STYLE}>{label}</span>
    </div>
  );
};

export default DraftWatermark;
